import { css } from "@emotion/react";
import { FC, KeyboardEvent, MouseEvent } from "react";
import { match } from "ts-pattern";

import {
  lgTableCellStyle,
  mdTableCellStyle,
  smTableCellStyle,
  tableCellStyle,
  xlTableCellStyle,
} from "~/components/database/detail/database-content/PcDatabaseContent";
import {
  OrderType,
  TableCellSize,
} from "~/components/database/detail/database-content/lib";
import { OrderIcon } from "~/components/database/detail/database-content/response-table/OrderIcon";

export type TabHeadingButtonsProps = {
  title: string;
  onClick?: (event: MouseEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  orderType?: OrderType;
  disabled?: boolean;
  size?: TableCellSize;
};

export const TableHeaderButton: FC<TabHeadingButtonsProps> = ({
  title,
  onClick,
  onKeyDown,
  orderType = "none",
  disabled,
  size,
}) => {
  const tableHeaderItemStyles = [tabHeadingButtonStyle, tableCellStyle];
  const cellStyle = match(size)
    .with(TableCellSize.sm, () => smTableCellStyle)
    .with(TableCellSize.md, () => mdTableCellStyle)
    .with(TableCellSize.lg, () => lgTableCellStyle)
    .with(TableCellSize.xl, () => xlTableCellStyle)
    .with(undefined, () => undefined)
    .exhaustive();
  cellStyle && tableHeaderItemStyles.push(cellStyle);

  return disabled ? (
    <div
      role="columnheader"
      css={[tabHeadingButtonStyle, tabHeadingDisabledButtonStyle]}
    >
      {title}
    </div>
  ) : (
    <div
      role="columnheader"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      css={tableHeaderItemStyles}
    >
      {title}
      <OrderIcon orderType={orderType} />
    </div>
  );
};

const tabHeadingButtonStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  background-color: #fff;
  color: #000;
  padding: 16px 24px;
  cursor: pointer;
  text-align: left;
  user-select: none;
  // NOTE: iOS の Safari では文字サイズを自動で調整する機能がついているので、勝手に文字サイズ変更が行われないようにする
  // https://developer.mozilla.org/en-US/docs/Web/CSS/text-size-adjust
  text-size-adjust: 100%;

  // NOTE: ソートのメタファーがホバーで認知できるようにするためのスタイル
  &:hover > span[aria-hidden="true"] {
    color: inherit;
  }
`;

const tabHeadingDisabledButtonStyle = css`
  cursor: initial;
`;
